import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { BigNumber, constants, utils, providers, BigNumberish } from 'ethers'
import { ExternalProvider } from '@ethersproject/providers'
import { Multicall, ContractCallContext } from 'ethereum-multicall'

import configs from 'configs'
import useClientWallet from './useClientWallet'
import useGlobalReducer from './useGlobalReducer'
import {
  usePMT,
  useGladiator,
  GladiatorTypes,
  useKnight,
  KnightTypes,
} from './contracts'
import {
  ERC20 as AbiERC20,
  Gladiator as AbiGladiator,
  Multicall2 as AbiMulticall2,
  Knight as AbiKnight,
} from './abi'
import formatWei from 'utils/formatWei'

export type { AppGlobalState } from './useGlobalReducer'

const debug = require('debug')('app:useGlobalStates')

const formatToken = formatWei(18, 3)
const REFRESH_TIMEOUT = 10000

export default function useGlobalStates() {
  const refreshStatus = useRef({
    pauseAutoRefresh: false,
    fetching: false,
    lastUpdate: 0,
    lastAutoUpdate: 0,
    lastUpdateSuccess: false,
    lastAccount: '',
  })
  const [lastAutoRefresh, triggerAutoRefresh] = useState(0)

  const wallet = useClientWallet()
  const [fullState, dispatch] = useGlobalReducer()

  const tokenPMT = usePMT(fullState.network.defaultProvider)
  const game = useGladiator(fullState.network.defaultProvider)
  const knight = useKnight(fullState.network.defaultProvider)

  const readonlyPMT = usePMT(fullState.network.providers.readonly)
  const readonlyGladiator = useGladiator(fullState.network.providers.readonly)
  const readonlyKnight = useKnight(fullState.network.providers.readonly)

  const play = useCallback(
    async (choice: GladiatorTypes.Gladiator, bet: number) => {
      if (wallet.status !== 'connected')
        throw new Error('Unable to play game before connecting')
      if (!game) throw new Error('Contract disconnected')
      refreshStatus.current.pauseAutoRefresh = true

      debug('Play -- bet amount: %d', bet)

      const { wait } = await game!.play(choice, {
        value: utils.parseEther(bet.toString()),
        gasLimit: 6000000,
      })

      const { events = [] } = await wait(1)

      debug('Play -- receive events: %o', events)

      const event = events.find(({ event }) => event === 'Play')
      refreshStatus.current.pauseAutoRefresh = false

      // Contract didn't emit Play event correctly.
      if (!event) throw new Error('Transaction failure, please try again.')

      const {
        args: {
          result: winner,
          amount,
          returnTt: earnedTT,
          returnToken: earnedPMT,
        },
      } = event as GladiatorTypes.Event.Play

      debug(
        'Play: bet %s on %d, result: %d won',
        amount.toString(),
        choice,
        winner.toNumber()
      )

      return {
        won: choice === winner.toNumber(),
        winner: winner.toNumber(),
        paid: utils.formatEther(amount),
        earnedTT: utils.formatEther(earnedTT),
        earnedPMT: utils.formatEther(earnedPMT),
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [fullState.network.defaultProvider]
  )

  const claim = useCallback(async () => {
    if (wallet.status !== 'connected')
      throw new Error('Unable to claim before connecting')
    if (!game) throw new Error('Contract disconnected')
    refreshStatus.current.pauseAutoRefresh = true

    const { wait } = await game!.claim({
      gasLimit: 1000000,
    })
    refreshStatus.current.pauseAutoRefresh = false

    const { events = [] } = await wait(1)

    debug('Claim -- receive events: %o', events)

    const event = events.find(({ event }) => event === 'Claim')

    if (!event) return { claimed: null }

    const {
      args: { claimAmount },
    } = event as GladiatorTypes.Event.Claim

    return {
      claimed: utils.formatEther(claimAmount),
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [fullState.network.defaultProvider])

  const joinKnights = useCallback(
    async (
      tokenId: number,
      stakingAmount: BigNumberish,
      total: BigNumberish
    ) => {
      if (wallet.status !== 'connected')
        throw new Error('Unable to join knights before connecting')
      if (!knight) throw new Error('Contract disconnected')
      refreshStatus.current.pauseAutoRefresh = true

      debug(
        'Knight::Join -- tokenId %d | staking: %s | total TT send: %s',
        tokenId,
        stakingAmount.toString(),
        total.toString()
      )

      const { wait } = await knight!.join(tokenId, stakingAmount, {
        value: total,
        gasLimit: 1000000,
      })

      const { events = [] } = await wait(1)

      debug('Knight::Join -- receive events: %o', events)

      const event = events.find(({ event }) => event === 'Join')
      refreshStatus.current.pauseAutoRefresh = false

      if (!event) throw new Error('Transaction failure, please try again.')

      const {
        args: { id, joiner, amount, fee },
      } = event as KnightTypes.Event.Join

      debug(
        'Knight::Join -- result: id: %s | joiner: %s | amount: %s | fee: %s',
        id.toString(),
        joiner,
        utils.formatEther(amount),
        utils.formatEther(fee)
      )

      return {
        tokenId: id,
        joiner,
        staked: amount,
        fee,
      }
    },
    [knight, wallet.status]
  )

  const leaveKnights = useCallback(
    async (tokenId: number) => {
      if (wallet.status !== 'connected')
        throw new Error('Unable to leave knights before connecting')
      if (!knight) throw new Error('Contract disconnected')
      refreshStatus.current.pauseAutoRefresh = true

      const { events = [] } = await knight!.leave(tokenId)
        .then(({ wait }) => wait(1))

      debug('Knight::leave -- receive events: %o', events)

      const event = events.find(({ event }) => event === 'Leave')
      refreshStatus.current.pauseAutoRefresh = false

      if (!event) throw new Error('Transaction failure, please try again.')

      const {
        args: { id, leaver, amount, profit, fee },
      } = event as KnightTypes.Event.Leave

      debug(
        'Knight::leave -- result: id: %s | leaver: %s | amount: %s | profit: %s | fee: %s',
        id.toString(),
        leaver,
        utils.formatEther(amount),
        utils.formatEther(profit),
        utils.formatEther(fee)
      )

      return {
        tokenId: id,
        leaver,
        staked: amount,
        profit, // 這邊不是實際給用戶的錢! 是 Staking 期間的總收入 + 取代者給的錢 (這邊主動離開不會有)
        fee,    // 給 team 的 fee
      }
    },
    [knight, wallet.status]
  )

  // QUEEN
  const harvest = useCallback(
    async () => {
      if (wallet.status !== 'connected')
        throw new Error('Unable to harvest before connecting')
      if (!knight) throw new Error('Contract disconnected')
      refreshStatus.current.pauseAutoRefresh = true

      const { events = [] } = await knight!.queen()
        .then(({ wait }) => wait(1))

      debug('Knight::queen -- receive events: %o', events)

      const event = events.find(({ event }) => event === 'Queen')
      refreshStatus.current.pauseAutoRefresh = false

      if (!event) throw new Error('Transaction failure, please try again.')

      const {
        args: { id, queen, profit, fee },
      } = event as KnightTypes.Event.Queen

      debug(
        'Knight::queen -- result: id: %s | queen: %s | profit: %s | fee: %s',
        id.toString(),
        queen,
        utils.formatEther(profit),
        utils.formatEther(fee)
      )

      return {
        queenId: id,
        owner: queen,
        profit,
        fee,
      }
    },
    [knight, wallet.status]
  )

  const setConnection = useCallback(
    async (method: 'injected' | 'walletconnect' | '') => {
      debug('switch connection to: %s', method)
      if (method !== '') {
        try {
          await wallet.connect!(method)
          dispatch({
            type: 'NETWORK_ERROR',
            payload: null,
          })
        } catch (error) {
          debug('Set connection error: %o', error)
          dispatch({
            type: 'NETWORK_ERROR',
            payload: error,
          })
        }
      } else {
        dispatch({
          type: 'SET_PROVIDER',
          payload: {
            isReadOnly: true,
            defaultProvider: fullState.network.providers.readonly,
          },
        })
      }
    },
    [fullState.network, wallet, dispatch]
  )

  const switchNetwork = useCallback(() => {
    if (window.ethereum) {
      window.ethereum
        .request({
          method: 'wallet_addEthereumChain',
          params: [configs.network],
        })
        .catch((error: any) => {
          dispatch({
            type: 'NETWORK_ERROR',
            payload: error,
          })
        })
    }
  }, [dispatch])

  const approvePMT = useCallback(async () => {
    if (wallet.status !== 'connected')
      throw new Error('Unable to approve before connecting')
    if (!tokenPMT) throw new Error('Contract disconnected')

    debug('Approve PMT')

    const knightAddress = fullState.configs.contract.Knight
    const { wait } = await tokenPMT?.approve(
      knightAddress,
      constants.MaxUint256
    )

    const { events = [] } = await wait(1)

    debug('Approve PMT -- receive events: %o', events)

    return events.some(({ event }) => event === 'Approval')
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [fullState.network.defaultProvider])

  const refreshGameInfo = useCallback(() => {
    const { current } = refreshStatus

    const gladiatorAddress =
      readonlyGladiator?.address ?? fullState.configs.contract.Gladiator
    const knightAddress =
      readonlyKnight?.address ?? fullState.configs.contract.Knight

    const tokenAddress =
      readonlyPMT?.address ?? fullState.configs.token.PMT.address
    const userAddress = wallet.account ?? ''
    const multicallAddress = fullState.configs.contract.Multicall2

    if (
      Date.now() - current.lastUpdate < 1000 &&
      current.lastUpdateSuccess &&
      current.lastAccount === userAddress
    )
      return Promise.reject('too soon')

    const multicall = new Multicall({
      ethersProvider: fullState.network.providers.readonly,
      tryAggregate: true,
      multicallCustomContractAddress: multicallAddress,
    })

    const requests: ContractCallContext[] = [
      {
        reference: 'BaseInfo',
        abi: AbiMulticall2,
        contractAddress: multicallAddress,
        calls: [
          {
            reference: 'blockNumber',
            methodName: 'getBlockNumber',
            methodParameters: [],
          },
          {
            reference: 'knightsVault',
            methodName: 'getEthBalance',
            methodParameters: [knightAddress],
          },
        ],
      },
      {
        reference: 'GladiatorInfoResponse',
        abi: AbiGladiator,
        contractAddress: configs.contract.Gladiator,
        calls: [
          {
            reference: 'latestWeek',
            methodName: 'latestWeek',
            methodParameters: [],
          },
          {
            reference: 'secondPerWeek',
            methodName: 'secondPerWeek',
            methodParameters: [],
          },
        ],
      },
      {
        reference: 'GladiatorChampionsResponse',
        abi: AbiGladiator,
        contractAddress: configs.contract.Gladiator,
        calls: [
          {
            reference: 'weekChampions1',
            methodName: 'weekChampions',
            methodParameters: [0],
          },
          {
            reference: 'weekChampions2',
            methodName: 'weekChampions',
            methodParameters: [1],
          },
          {
            reference: 'weekChampions3',
            methodName: 'weekChampions',
            methodParameters: [2],
          },
        ],
      },
      {
        reference: 'GladiatorChampionRatioResponse',
        abi: AbiGladiator,
        contractAddress: configs.contract.Gladiator,
        calls: [
          {
            reference: 'championRatio1',
            methodName: 'championRatio',
            methodParameters: [0],
          },
          {
            reference: 'championRatio2',
            methodName: 'championRatio',
            methodParameters: [1],
          },
          {
            reference: 'championRatio3',
            methodName: 'championRatio',
            methodParameters: [2],
          },
        ],
      },
      {
        reference: 'KnightResponse',
        abi: AbiKnight,
        contractAddress: configs.contract.Knight,
        calls: [
          { reference: 'queenId', methodName: 'queenId', methodParameters: [] },
          {
            reference: 'seatFeeRatio',
            methodName: 'seatFeeRatio',
            methodParameters: [],
          },
          {
            reference: 'secondPerWeek',
            methodName: 'secondPerWeek',
            methodParameters: [],
          },
          {
            reference: 'maxBet',
            methodName: 'maxBet',
            methodParameters: [],
          },
          {
            reference: 'punishRatio',
            methodName: 'punishRatio',
            methodParameters: [],
          },
          { reference: 'minJoin', methodName: 'minJoin', methodParameters: [] },

          ...new Array(12).fill(0).map((_, i) => ({
            reference: `knights-${i + 1}`,
            methodName: 'knights',
            methodParameters: [i + 1],
          })),
        ],
      },
      {
        reference: 'SeatsOwnerResponse',
        abi: AbiKnight,
        contractAddress: configs.contract.Knight,
        calls: new Array(12).fill(0).map((_, i) => ({
          reference: `${i + 1}`,
          methodName: 'ownerOf',
          methodParameters: [i + 1],
        })),
      },
      {
        reference: 'SeatsProfitResponse',
        abi: AbiKnight,
        contractAddress: configs.contract.Knight,
        calls: new Array(12).fill(0).map((_, i) => ({
          reference: `${i + 1}`,
          methodName: 'earned',
          methodParameters: [i + 1],
        })),
      },
    ]

    if (userAddress) {
      requests.push(
        // UserPMT
        {
          reference: 'UserPMT',
          contractAddress: tokenAddress,
          abi: AbiERC20,
          calls: [
            {
              reference: 'allowance',
              methodName: 'allowance',
              methodParameters: [userAddress, knightAddress],
            },
            {
              reference: 'balance',
              methodName: 'balanceOf',
              methodParameters: [userAddress],
            },
          ],
        },
        // UserTT
        {
          reference: 'UserTT',
          contractAddress: multicallAddress,
          abi: AbiMulticall2,
          calls: [
            {
              reference: 'user',
              methodName: 'getEthBalance',
              methodParameters: [userAddress],
            },
          ],
        },
        // UserGladiatorResponse -> 玩遊戲統計數據
        {
          reference: 'UserGladiatorResponse',
          contractAddress: gladiatorAddress,
          abi: AbiGladiator,
          calls: [
            {
              reference: 'info',
              methodName: 'infoOf',
              methodParameters: [userAddress],
            },
          ],
        }
      )
    }

    Object.assign(current, {
      fetching: true,
      lastAccount: userAddress,
      lastSuccess: false,
      lastUpdate: Date.now(),
    })

    return multicall
      .call(requests)
      .then(
        ({
          results: {
            BaseInfo,
            GladiatorInfoResponse,
            GladiatorChampionsResponse,
            GladiatorChampionRatioResponse,
            KnightResponse,
            SeatsOwnerResponse,
            SeatsProfitResponse,
            UserGladiatorResponse,
            UserPMT,
            UserTT,
          },
        }) => {
          const knight: any = {}
          const gladiator: any = {}
          const balances: any = {}

          const [blockNumber, vaultAssets] = BaseInfo.callsReturnContext
          knight.vault = vaultAssets.success
            ? formatToken(vaultAssets.returnValues[0])
            : '0'

          const [
            queenIdResponse,
            seatFeeRatioResponse,
            knightSecondsPerWeek,
            maxBetResponse,
            punishRatioResponse,
            minJoinResponse,
            ...knightsResponse
          ] = KnightResponse.callsReturnContext

          knight.queenId = queenIdResponse.success
            ? BigNumber.from(queenIdResponse.returnValues[0]).toNumber()
            : 0
          knight.seatFee = seatFeeRatioResponse.success
            ? Number(
                utils.formatUnits(
                  BigNumber.from(seatFeeRatioResponse.returnValues[0]),
                  6
                )
              )
            : 0.05
          knight.secondPerWeek = knightSecondsPerWeek.success
            ? BigNumber.from(knightSecondsPerWeek.returnValues[0]).toNumber()
            : 604800
          knight.maxBet = maxBetResponse.success
            ? Number(
                utils.formatEther(
                  BigNumber.from(maxBetResponse.returnValues[0])
                )
              )
            : 3000
          knight.punishRatio = punishRatioResponse.success
            ? Number(
                utils.formatUnits(
                  BigNumber.from(punishRatioResponse.returnValues[0]),
                  6
                )
              )
            : 0.05
          knight.minJoin = minJoinResponse.success
            ? Number(
                utils.formatEther(
                  BigNumber.from(minJoinResponse.returnValues[0])
                )
              )
            : 0

          const owners = SeatsOwnerResponse.callsReturnContext.map((owner) =>
            owner.success && owner.returnValues[0] !== constants.AddressZero
              ? owner.returnValues[0]
              : null
          )

          const profits = SeatsProfitResponse.callsReturnContext.map(
            (result) => {
              if (!result.success) return 0
              const profit = BigNumber.from(result.returnValues[0])
              const lost = BigNumber.from(result.returnValues[1])

              if (lost.gt(0)) return Number(utils.formatEther(lost)) * -1
              return Number(utils.formatEther(profit))
            }
          )

          knight.list = knightsResponse.map(({ success, returnValues }, i) =>
            success
              ? {
                  id: i + 1,
                  owner: owners[i],
                  amount: Number(
                    utils.formatEther(BigNumber.from(returnValues[2]))
                  ),
                  since: BigNumber.from(returnValues[0]).toNumber(),
                  startIndex: BigNumber.from(returnValues[1]),
                  profit: profits[i],
                }
              : {
                  error: true,
                  id: i + 1,
                  owner: owners[i],
                  amount: 0,
                  since: 0,
                  startIndex: constants.Zero,
                  profit: profits[i],
                }
          )

          const [allowancePMT, balancePMT] = UserPMT?.callsReturnContext ?? []
          if (allowancePMT?.success)
            knight.pmtAllowance = BigNumber.from(allowancePMT.returnValues[0])
          if (balancePMT?.success)
            balances.PMT = {
              amount: BigNumber.from(balancePMT.returnValues[0]),
              display: formatToken(balancePMT.returnValues[0]),
            }

          const [balanceTT] = UserTT?.callsReturnContext ?? []
          if (balanceTT?.success)
            balances.TT = {
              amount: BigNumber.from(balanceTT.returnValues[0]),
              display: formatToken(balanceTT.returnValues[0]),
            }

          const [gladiatorLatestWeek, gladiatorSecondPerWeek] = GladiatorInfoResponse.callsReturnContext
          // console.log('GLAD', gladiatorLatestWeek, gladiatorSecondPerWeek)
          const [gladiatorLastRound] = gladiatorLatestWeek.returnValues
          gladiator.secondsPerRound = BigNumber.from(gladiatorSecondPerWeek.returnValues[0]).toNumber()
          gladiator.lastRound = (gladiatorLastRound) * gladiator.secondsPerRound * 1000
          gladiator.nextRound = (gladiatorLastRound + 1) * gladiator.secondsPerRound * 1000
          gladiator.champions = GladiatorChampionsResponse.callsReturnContext.map(
            ({ success, returnValues }, i) => ({
              address: success && returnValues[0] !== constants.AddressZero ? returnValues[0] : null,
              ratio: GladiatorChampionRatioResponse.callsReturnContext[i].success
                ? utils.formatUnits(BigNumber.from(GladiatorChampionRatioResponse.callsReturnContext[i].returnValues[0]), 6)
                : 0
            })
          )
           knightsResponse.map(({ success, returnValues }, i) =>
            success
              ? {
                  id: i + 1,
                  owner: owners[i],
                  amount: Number(
                    utils.formatEther(BigNumber.from(returnValues[2]))
                  ),
                  since: BigNumber.from(returnValues[0]).toNumber(),
                  startIndex: BigNumber.from(returnValues[1]),
                  profit: profits[i],
                }
              : {
                  error: true,
                  id: i + 1,
                  owner: owners[i],
                  amount: 0,
                  since: 0,
                  startIndex: constants.Zero,
                  profit: profits[i],
                }
          )

          if (UserGladiatorResponse) {
            const [info] = UserGladiatorResponse.callsReturnContext

            if (info.success) {
              const [total, claimable, lastSeen, weekPlayed] = info.returnValues
              gladiator.total = formatToken(total)
              gladiator.claimables = formatToken(claimable)
              gladiator.lastSeen = lastSeen > 0 ? new Date(lastSeen * 1000) : null
              gladiator.weekPlayed = formatToken(weekPlayed)
            }
          }

          const network = blockNumber.success
            ? {
                blockNumber: BigNumber.from(
                  blockNumber.returnValues[0]
                ).toNumber(),
              }
            : null

          ///////// STEP 2 /////////

          const extraRequests: ContractCallContext[] = []
          const champions: {
            address: string|null
            ratio: number
          }[] = gladiator.champions

          if (champions.some(({ address }) => address)) {
            extraRequests.push({
              reference: 'ChampionInfo',
              abi: AbiGladiator,
              contractAddress: fullState.configs.contract.Gladiator,
              calls: champions
                .filter(({ address }) => address)
                .map(({ address }) => ({
                  reference: address as string,
                  methodName: 'infoOf',
                  methodParameters: [address],
                }))
            })
          }

          if (extraRequests.length) {
            return multicall
              .call(extraRequests)
              .then(({
                results: {
                  ChampionInfo,
                },
              }) => {
                ChampionInfo.callsReturnContext.forEach((response) => {
                  if (response.success) {
                    // const address = response.reference
                    const champion = champions.find(({ address }) => address === response.reference) as {
                      address: string
                      ratio: number
                      claimable?: number
                      weekPlayed?: number
                    }

                    const [/* total */, claimable, /* lastSeen */, weekPlayed] = response.returnValues

                    champion.claimable = Number(utils.formatEther(BigNumber.from(claimable)))
                    champion.weekPlayed = Number(utils.formatEther(BigNumber.from(weekPlayed)))
                  }
                })

                dispatch({
                  type: 'GAME_STATE',
                  payload: {
                    balances,
                    gladiator,
                    knight,
                    network,
                  },
                })
              })
          } else {
            dispatch({
              type: 'GAME_STATE',
              payload: {
                balances,
                gladiator,
                knight,
                network,
              },
            })
          }
        }
      )
      .then(() => {
        Object.assign(current, {
          fetching: false,
          lastAccount: userAddress,
          lastSuccess: true,
          lastUpdate: Date.now(),
        })
      })
      .catch((e) => {
        console.warn('Refresh game info failure', e)
        Object.assign(current, {
          fetching: false,
          lastAccount: userAddress,
          lastSuccess: false,
          lastUpdate: Date.now(),
        })
      })
      // 避免檢查許多不會變動的 contract address 與 readonly provider
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [fullState.network.defaultProvider, wallet.account, dispatch])

  const showError = useCallback(
    (error: Error | string) => dispatch({ type: 'ERROR', payload: error }),
    [dispatch]
  )

  const clearError = useCallback(
    () => dispatch({ type: 'ERROR', payload: null }),
    [dispatch]
  )

  // on wallet initialized
  useEffect(() => {
    refreshGameInfo().catch((e) =>
      debug('Refresh game info on initialing failure, error: %s', e)
    )
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [fullState.network.defaultProvider])

  // update default provider
  useEffect(() => {
    if (wallet.status === 'connected') {
      const { ethereum } = wallet
      const provider = new providers.Web3Provider(ethereum as ExternalProvider)
      const signer = provider.getSigner().connectUnchecked()
      dispatch({
        type: 'SET_PROVIDER',
        payload: {
          providers: {
            web3: provider,
          },
          isReadOnly: false,
          defaultProvider: signer,
        },
      })
    } else if (
      fullState.network.defaultProvider !== fullState.network.providers.readonly
    ) {
      dispatch({
        type: 'SET_PROVIDER',
        payload: {
          defaultProvider: fullState.network.providers.readonly,
          isReadOnly: true,
        },
      })
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [wallet.status])

  // auto refresh
  useEffect(() => {
    if (
      !document.hidden &&
      !refreshStatus.current.pauseAutoRefresh &&
      refreshStatus.current.lastUpdate + REFRESH_TIMEOUT < Date.now()
    ) {
      refreshGameInfo()
        .then(() => {
          refreshStatus.current.lastAutoUpdate = Date.now()
        })
        .catch((e) => debug('Auto refresh failure, error: %s', e))
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [lastAutoRefresh])

  useEffect(() => {
    const timer = window.setInterval(() => {
      triggerAutoRefresh(Date.now())
    }, REFRESH_TIMEOUT)
    return () => window.clearInterval(timer)
  }, [])

  const actions = useMemo(() => ({
    // General ------------
    refreshGameInfo,
    showError,
    clearError,

    // Wallet
    switchNetwork,
    setConnection,

    // Tokens -------------
    approvePMT,

    // Gladiator ----------
    play,
    claim,

    // Knights ------------
    joinKnights,
    leaveKnights,
    harvest,
  }), [
    approvePMT,
    claim,
    clearError,
    joinKnights,
    leaveKnights,
    harvest,
    play,
    refreshGameInfo,
    setConnection,
    showError,
    switchNetwork,
  ])

  return useMemo(() => ({
    ...fullState,
    wallet,
    actions,
  }), [actions, fullState, wallet])
}
